import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { curveMonotoneX, line } from 'd3-shape';
import { scaleLinear } from 'd3-scale';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { ChartHScroll, formatCurrency, formatK, MIN_PX_PER_CALENDAR_YEAR, thinTicks, useChartPlotWidth } from './d3';
import { SvgChartCanvas } from './web/SvgChartCanvas.web';
import type { AmortizationRow } from '../../utils/loanAmortization';
import {
  balanceAtYear,
  calendarYearForOffset,
  formatPayoffMonth,
  interestPaidThroughYear,
} from '../../utils/loanAmortization';

type SeriesPoint = { year: number; balance: number };

type LoanPayoffLineChartProps = {
  baseline: SeriesPoint[];
  accelerated: SeriesPoint[];
  baselineRows: AmortizationRow[];
  acceleratedRows: AmortizationRow[];
  showComparison: boolean;
  startMonth?: string | null;
  height?: number;
};

const MARGIN = { top: 18, right: 14, bottom: 28, left: 44 };
const HIT = 22;

function payoffYear(series: SeriesPoint[]): number {
  const zero = series.find((p) => p.balance <= 0);
  if (zero) return zero.year;
  return series[series.length - 1]?.year ?? 0;
}

export function LoanPayoffLineChart({
  baseline,
  accelerated,
  baselineRows,
  acceleratedRows,
  showComparison,
  startMonth = null,
  height = 180,
}: LoanPayoffLineChartProps) {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const seriesB = showComparison ? accelerated : baseline;
  const all = [...baseline, ...seriesB];
  const minYear = all.length > 0 ? Math.min(...all.map((p) => p.year)) : 0;
  const maxYear = all.length > 0 ? Math.max(...all.map((p) => p.year)) : 0;
  const maxY = Math.max(...all.map((p) => p.balance), 1);
  const { plotWidth: width, scrolls, onLayout } = useChartPlotWidth({
    unitCount: Math.max(maxYear - minYear, 1),
    minPxPerUnit: MIN_PX_PER_CALENDAR_YEAR,
    leftMargin: MARGIN.left,
    rightMargin: MARGIN.right,
  });

  const model = useMemo(() => {
    if (all.length === 0 || width <= 0) {
      return {
        pathA: '',
        pathB: '',
        dots: [] as { year: number; x: number; yA: number; yB: number }[],
        yTicks: [] as { value: number; y: number }[],
        xTicks: [] as { year: number; x: number; label: string }[],
        gridLines: [] as number[],
      };
    }

    const innerWidth = width - MARGIN.left - MARGIN.right;
    const innerHeight = height - MARGIN.top - MARGIN.bottom;

    const xScale = scaleLinear()
      .domain([minYear, maxYear])
      .range([MARGIN.left, MARGIN.left + innerWidth]);

    const yScale = scaleLinear()
      .domain([0, maxY])
      .nice()
      .range([MARGIN.top + innerHeight, MARGIN.top]);

    const lineGen = line<SeriesPoint>()
      .x((d) => xScale(d.year))
      .y((d) => yScale(d.balance))
      .curve(curveMonotoneX);

    const years = Array.from(
      new Set([...baseline, ...seriesB].map((p) => p.year)),
    ).sort((a, b) => a - b);

    const yTickValues = yScale.ticks(4);
    const xTickYears = thinTicks(years, innerWidth);

    return {
      pathA: lineGen(baseline) ?? '',
      pathB: lineGen(seriesB) ?? '',
      dots: years.map((year) => ({
        year,
        x: xScale(year),
        yA: yScale(balanceAtYear(baseline, year)),
        yB: yScale(balanceAtYear(seriesB, year)),
      })),
      yTicks: yTickValues.map((value) => ({ value, y: yScale(value) })),
      xTicks: xTickYears.map((year) => ({
        year,
        x: xScale(year),
        label:
          calendarYearForOffset(startMonth, year) != null
            ? String(calendarYearForOffset(startMonth, year))
            : String(year),
      })),
      gridLines: yTickValues.map((tick) => yScale(tick)),
    };
  }, [width, height, baseline, seriesB, minYear, maxYear, maxY, startMonth]);

  const tooltip = useMemo(() => {
    if (selectedYear == null) return null;
    const scheduled = balanceAtYear(baseline, selectedYear);
    const withExtras = balanceAtYear(accelerated, selectedYear);
    const ahead = Math.max(0, scheduled - withExtras);
    const interestSaved = Math.max(
      0,
      interestPaidThroughYear(baselineRows, selectedYear) -
        interestPaidThroughYear(acceleratedRows, selectedYear),
    );
    return { year: selectedYear, scheduled, withExtras, ahead, interestSaved };
  }, [selectedYear, baseline, accelerated, baselineRows, acceleratedRows]);

  return (
    <View>
      <ChartHScroll
        height={height}
        plotWidth={width}
        scrolls={scrolls}
        onLayout={onLayout}
        style={styles.chartArea}
        axisWidth={MARGIN.left}
        axis={
          <>
            <Text style={styles.yCaption}>$</Text>
            {model.yTicks.map((tick) => (
              <Text
                key={`y-${tick.value}`}
                style={[styles.yTick, { top: tick.y - 7 }]}
              >
                {formatK(tick.value)}
              </Text>
            ))}
          </>
        }
      >
        {width > 0 && (
          <>
            <SvgChartCanvas
              width={width}
              height={height}
              style={{ pointerEvents: 'none' }}
            >
              {model.gridLines.map((y, i) => (
                <line
                  key={`grid-${i}`}
                  x1={MARGIN.left}
                  y1={y}
                  x2={width - MARGIN.right}
                  y2={y}
                  stroke={colors.border}
                  strokeWidth={1}
                  opacity={0.45}
                />
              ))}
              <line
                x1={MARGIN.left}
                y1={MARGIN.top}
                x2={MARGIN.left}
                y2={height - MARGIN.bottom}
                stroke={colors.textMuted}
                strokeWidth={1}
              />
              <line
                x1={MARGIN.left}
                y1={height - MARGIN.bottom}
                x2={width - MARGIN.right}
                y2={height - MARGIN.bottom}
                stroke={colors.textMuted}
                strokeWidth={1}
              />
              {model.yTicks.map((tick) => (
                <line
                  key={`yt-${tick.value}`}
                  x1={MARGIN.left - 4}
                  y1={tick.y}
                  x2={MARGIN.left}
                  y2={tick.y}
                  stroke={colors.textMuted}
                  strokeWidth={1}
                />
              ))}
              {model.xTicks.map((tick) => (
                <line
                  key={`xt-${tick.year}`}
                  x1={tick.x}
                  y1={height - MARGIN.bottom}
                  x2={tick.x}
                  y2={height - MARGIN.bottom + 4}
                  stroke={colors.textMuted}
                  strokeWidth={1}
                />
              ))}
              <path d={model.pathA} fill="none" stroke={colors.warning} strokeWidth={2.5} />
              {showComparison && (
                <path d={model.pathB} fill="none" stroke={colors.accent} strokeWidth={2.5} />
              )}
              {model.dots.map((dot) => {
                const selected = selectedYear === dot.year;
                return (
                  <React.Fragment key={dot.year}>
                    <circle
                      cx={dot.x}
                      cy={dot.yA}
                      r={selected ? 5 : 3.5}
                      fill={colors.warning}
                    />
                    {showComparison && (
                      <circle
                        cx={dot.x}
                        cy={dot.yB}
                        r={selected ? 5 : 3.5}
                        fill={colors.accent}
                      />
                    )}
                  </React.Fragment>
                );
              })}
              {model.xTicks.map((tick) => (
                <text
                  key={`xl-${tick.year}`}
                  x={tick.x}
                  y={height - 8}
                  textAnchor="middle"
                  fill={colors.text}
                  fontSize={10}
                  fontWeight={600}
                  fontFamily="IBM Plex Sans, sans-serif"
                >
                  {tick.label}
                </text>
              ))}
            </SvgChartCanvas>
            {model.dots.map((dot) => (
              <Pressable
                key={`hit-${dot.year}`}
                accessibilityRole="button"
                accessibilityLabel={`Year ${calendarYearForOffset(startMonth, dot.year) ?? dot.year} details`}
                onPress={() =>
                  setSelectedYear((prev) => (prev === dot.year ? null : dot.year))
                }
                style={[
                  styles.hit,
                  {
                    left: dot.x - HIT / 2,
                    height: height - MARGIN.bottom,
                  },
                ]}
              />
            ))}
          </>
        )}
      </ChartHScroll>
      <Text style={styles.axisTitle}>Year</Text>

      {tooltip && (
        <View style={styles.tooltip}>
          <Text style={styles.tooltipTitle}>
            {calendarYearForOffset(startMonth, tooltip.year) != null
              ? String(calendarYearForOffset(startMonth, tooltip.year))
              : `Year ${tooltip.year}`}
          </Text>
          <Text style={styles.tooltipRow}>
            Scheduled balance{' '}
            <Text style={styles.tooltipWarn}>{formatCurrency(tooltip.scheduled)}</Text>
          </Text>
          {showComparison && (
            <>
              <Text style={styles.tooltipRow}>
                With extras{' '}
                <Text style={styles.tooltipAccent}>
                  {formatCurrency(tooltip.withExtras)}
                </Text>
              </Text>
              {tooltip.ahead > 0 && (
                <Text style={styles.tooltipRow}>
                  Balance ahead{' '}
                  <Text style={styles.tooltipAccent}>
                    {formatCurrency(tooltip.ahead)}
                  </Text>
                </Text>
              )}
              {tooltip.interestSaved > 0 && (
                <Text style={styles.tooltipSaved}>
                  Interest saved so far {formatCurrency(tooltip.interestSaved)}
                </Text>
              )}
            </>
          )}
          <Text style={styles.tooltipHint}>Tap a dot again to dismiss</Text>
        </View>
      )}

      <Text style={styles.axisHint}>Tap a year for remaining balance</Text>
      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: colors.warning }]} />
          <View style={styles.legendCopy}>
            <Text style={styles.legendText}>Scheduled payments</Text>
            <Text style={styles.legendValue}>
              {formatPayoffMonth(startMonth, baselineRows.length)
                ? `Paid off ${formatPayoffMonth(startMonth, baselineRows.length)}`
                : `Paid off in year ${payoffYear(baseline)}`}
            </Text>
          </View>
        </View>
        {showComparison && (
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: colors.accent }]} />
            <View style={styles.legendCopy}>
              <Text style={styles.legendText}>With extras</Text>
              <Text style={styles.legendValue}>
                {formatPayoffMonth(startMonth, acceleratedRows.length)
                  ? `Paid off ${formatPayoffMonth(startMonth, acceleratedRows.length)}`
                  : `Paid off in year ${payoffYear(accelerated)}`}
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  chartArea: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  hit: {
    position: 'absolute',
    top: 0,
    width: HIT,
    zIndex: 2,
    cursor: 'pointer' as const,
  },
  yCaption: {
    position: 'absolute',
    top: 2,
    left: 4,
    width: MARGIN.left - 8,
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'right',
  },
  yTick: {
    position: 'absolute',
    left: 4,
    width: MARGIN.left - 8,
    ...typography.caption,
    color: colors.text,
    fontSize: 10,
    fontVariant: ['tabular-nums'],
    textAlign: 'right',
  },
  axisTitle: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  tooltip: {
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: 'rgba(26, 34, 48, 0.95)',
    borderWidth: 1,
    borderColor: colors.glassBorder,
    gap: 4,
  },
  tooltipTitle: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
    marginBottom: 2,
  },
  tooltipRow: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  tooltipWarn: {
    color: colors.warning,
    fontWeight: '600',
  },
  tooltipAccent: {
    color: colors.accent,
    fontWeight: '600',
  },
  tooltipSaved: {
    ...typography.body,
    fontSize: 14,
    color: colors.accent,
    fontWeight: '600',
    marginTop: spacing.xs,
    fontVariant: ['tabular-nums'],
  },
  tooltipHint: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: spacing.xs,
    fontSize: 11,
  },
  axisHint: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  legend: {
    flexDirection: 'row',
    gap: spacing.lg,
    marginTop: spacing.md,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    flex: 1,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 4,
  },
  legendCopy: {
    flex: 1,
    gap: 2,
  },
  legendText: {
    ...typography.caption,
    color: colors.text,
  },
  legendValue: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
});
